/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useState } from 'react';
import classNames from 'classnames';
import { withRouter } from 'react-router-dom';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser, capitalizeFirstLetter } from '../../../../util/data';

import {
  AvatarLarge,
  ExternalLink,
  IconClose,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
  ResponsiveImage,
} from '../../../../components';
import sideChevIcon from '../../../../assets/sideChev.png';
import messageIcon from '../../../../assets/message.svg';
import userIcon from '../../../../assets/user.svg';
import accountIcon from '../../../../assets/account.png';
import dollarIcon from '../../../../assets/dollar.svg';
import bidsHandIcon from '../../../../assets/bidshand.svg';
import groupIcon from '../../../../assets/Group.svg';
import ticketIcon from '../../../../assets/Ticket.svg';
import profileHolderImage from '../../../../assets/profileHolder.png';

import { SvgIcon } from '../../../../components';

import css from './TopbarMobileMenu.module.css';

// Avatar image variants (same as Avatar component)
const AVATAR_IMAGE_VARIANTS = [
  'square-xsmall', // 40x40
  'square-xsmall2x', // 80x80
  'square-small', // 240x240
  'square-small2x', // 480x480
];

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = withRouter(
  ({
    isAuthenticated,
    currentUserHasListings,
    currentUser,
    onClose,
    onLogout,
    notificationCount,
    streamUnseenCount = 0,
    currentPage,
    customLinks,
    history,
  }) => {
    const user = ensureCurrentUser(currentUser);
    const [expandedSections, setExpandedSections] = useState({
      social: false,
      account: false,
    });
    const customCloseButton = (
      <button
        type="button"
        className={css.customCloseButton}
        onClick={onClose}
        aria-label="Close menu"
      >
        <span className={css.customCloseText}>Close</span>
        <IconClose rootClassName={css.customCloseIcon} />
      </button>
    );

    // Email verification check helper
    const checkEmailVerification = () => {
      const isEmailVerified = currentUser?.attributes?.emailVerified;
      if (isEmailVerified !== true) {
        history.push('/verify-email');
        return false;
      }
      return true;
    };

    // Toggle section expansion
    const toggleSection = section => {
      setExpandedSections(prev => ({
        ...prev,
        [section]: !prev[section],
      }));
    };

    // Handle navigation
    const handleNavigation = path => {
      history.push(path);
    };

    if (!isAuthenticated) {
      const signup = (
        <NamedLink name="SignupPage" className={css.signupLink}>
          <FormattedMessage id="TopbarMobileMenu.signupLink" />
        </NamedLink>
      );

      const login = (
        <NamedLink name="LoginPage" className={css.loginLink}>
          <FormattedMessage id="TopbarMobileMenu.loginLink" />
        </NamedLink>
      );

      const signupOrLogin = (
        <span className={css.authenticationLinks}>
          {signup}
          <span className={css.authenticationConnector}>
            {' '}
            <FormattedMessage
              id="TopbarMobileMenu.signupOrLoginConnector"
              defaultMessage="or"
            />{' '}
          </span>
          {login}
        </span>
      );

      return (
        <div className={css.root}>
          {customCloseButton}
          <div className={css.content}>
            <div className={css.authenticationGreeting}>
              <FormattedMessage
                id="TopbarMobileMenu.unauthorizedGreeting"
                values={{ lineBreak: <br />, signupOrLogin }}
              />
            </div>

            <div className={css.customLinksWrapper}>
              {customLinks.map((linkConfig, index) => (
                <CustomLinkComponent
                  key={`${linkConfig.text}_${index}`}
                  linkConfig={linkConfig}
                  currentPage={currentPage}
                />
              ))}
            </div>

            <div className={css.spacer} />
          </div>
        </div>
      );
    }

    const displayName = capitalizeFirstLetter(user.attributes.profile.firstName || '');

    return (
      <div className={css.root}>
        {customCloseButton}
        <div className={css.content}>
          {/* User Avatar and Greeting */}
          <div className={css.mobileUserInfo}>
            <div className={css.mobileAvatar}>
              {user.profileImage && user.profileImage.id ? (
                <ResponsiveImage
                  rootClassName={css.mobileAvatarImage}
                  alt={displayName}
                  image={user.profileImage}
                  variants={AVATAR_IMAGE_VARIANTS}
                  sizes="48px"
                />
              ) : (
                <img src={profileHolderImage} alt={displayName} className={css.mobileAvatarImage} />
              )}
            </div>
            <div className={css.mobileGreeting}>Activity &amp; Settings</div>
          </div>

          {/* Mobile Navigation Menu */}
          <div className={css.mobileNavMenu}>
            {/* Messages - moved to top */}
            <button
              className={css.mobileNavItem}
              onClick={() => {
                if (checkEmailVerification()) {
                  const inboxTab = currentUserHasListings ? 'sales' : 'orders';
                  history.push(`/inbox/${inboxTab}`);
                }
              }}
            >
              <SvgIcon aria-hidden className={css.mobileNavIcon} src={messageIcon} alt="" />
              Messages
              {notificationCount > 0 && <div className={css.notificationDotText} />}
            </button>

            {/* Profile */}
            <button
              className={css.mobileNavItem}
              onClick={() => handleNavigation('/user-settings')}
            >
              <SvgIcon aria-hidden className={css.profileNavIcon} src={userIcon} alt="" />
              Profile
            </button>

            {/* Account Section */}
            <div className={css.mobileNavSection}>
              <button
                className={`${css.mobileNavItem} ${css.mobileNavItemWithChevron}`}
                onClick={() => toggleSection('account')}
              >
                <span className={css.mobileNavItemContent}>
                  <SvgIcon aria-hidden className={css.accountNavIcon} src={accountIcon} alt="" />
                  Account
                </span>
                <img
                  src={sideChevIcon}
                  alt="Expand/Collapse"
                  className={`${css.mobileChevron} ${
                    expandedSections.account ? css.chevronExpanded : css.chevronCollapsed
                  }`}
                />
              </button>

              {expandedSections.account && (
                <div className={css.mobileSubMenu}>
                  <button
                    className={css.mobileSubItem}
                    onClick={() =>
                      handleNavigation('/user-settings?section=account&subsection=contact-details')
                    }
                  >
                    Contact details
                  </button>
                  <button
                    className={css.mobileSubItem}
                    onClick={() =>
                      handleNavigation('/user-settings?section=account&subsection=password')
                    }
                  >
                    Password
                  </button>
                  <button
                    className={css.mobileSubItem}
                    onClick={() =>
                      handleNavigation('/user-settings?section=account&subsection=payout-details')
                    }
                  >
                    Payout details
                  </button>
                  <button
                    className={css.mobileSubItem}
                    onClick={() =>
                      handleNavigation('/user-settings?section=account&subsection=payment-methods')
                    }
                  >
                    Payment methods
                  </button>
                </div>
              )}
            </div>

            <button
              className={css.mobileNavItem}
              onClick={() => {
                if (checkEmailVerification()) {
                  history.push('/l/new');
                }
              }}
            >
              <SvgIcon aria-hidden className={css.mobileNavIcon} src={dollarIcon} alt="" />
              Sell tickets
            </button>

            <button
              className={css.mobileNavItem}
              onClick={() => {
                if (checkEmailVerification()) {
                  history.push(
                    '/l/draft/00000000-0000-0000-0000-000000000000/new/details?listingType=testing-bids'
                  );
                }
              }}
            >
              <SvgIcon aria-hidden className={css.mobileNavIcon} src={bidsHandIcon} alt="" />
              Create bid
            </button>

            {/* Social Section */}
            <div className={css.mobileNavSection}>
              <button
                className={`${css.mobileNavItem} ${css.mobileNavItemWithChevron}`}
                onClick={() => toggleSection('social')}
              >
                <span className={css.mobileNavItemContent}>
                  <SvgIcon aria-hidden className={css.mobileNavIcon} src={groupIcon} alt="" />
                  Social
                </span>
                <img
                  src={sideChevIcon}
                  alt="Expand/Collapse"
                  className={`${css.mobileChevron} ${
                    expandedSections.social ? css.chevronExpanded : css.chevronCollapsed
                  }`}
                />
              </button>

              {expandedSections.social && (
                <div className={css.mobileSubMenu}>
                  <button
                    className={css.mobileSubItem}
                    onClick={() =>
                      handleNavigation('/user-settings?section=social&subsection=my-activity')
                    }
                  >
                    Activity
                  </button>
                  <button
                    className={css.mobileSubItem}
                    onClick={() =>
                      handleNavigation('/user-settings?section=social&subsection=my-interests')
                    }
                  >
                    Interests
                  </button>
                  <button
                    className={css.mobileSubItem}
                    onClick={() => {
                      if (checkEmailVerification()) {
                        history.push('/find-crews?filter=my-crews');
                      }
                    }}
                  >
                    Crews
                  </button>
                  <button
                    className={css.mobileSubItem}
                    onClick={() => {
                      if (checkEmailVerification()) {
                        history.push('/linkup-chat');
                      }
                    }}
                  >
                    LinkUps
                  </button>
                  <button
                    className={css.mobileSubItem}
                    onClick={() =>
                      handleNavigation('/user-settings?section=social&subsection=moderation')
                    }
                  >
                    Safety controls
                  </button>
                </div>
              )}
            </div>

            {/* My listings */}
            <button
              className={css.mobileNavItem}
              onClick={() => handleNavigation('/user-settings?section=my-listings')}
            >
              <SvgIcon aria-hidden className={css.mobileNavIcon} src={ticketIcon} alt="" />
              My listings
            </button>
          </div>
        </div>

        {/* Footer with Logout */}
        <div className={css.footer}>
          <button className={css.mobileLogoutButton} onClick={onLogout}>
            Log out
          </button>
        </div>
      </div>
    );
  }
);

export default TopbarMobileMenu;
